import React, { useRef, useState } from 'react'
import { FileText, Paperclip, Plus, Upload, X } from 'lucide-react'
import { toast } from 'sonner'
import { FILE_INPUT_ACCEPT } from '../config'
import { formatFileSize } from '../hooks/utils'
import { AttachmentList } from './AttachmentList'
import type { AttachmentsSectionProps } from '../types'
import { useAttachments } from '@/lib/hooks/useAttachments'
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible'
import { CollapsibleSectionHeader } from '@/components/app/layout/collapsible-section-header'
import { Button } from '@/components/ui/button'
import { logger } from '@/lib/monitoring/logger'

/**
 * Complete attachments section with upload and list functionality
 * Use this in task details, comments, etc.
 */
export const AttachmentsSection: React.FC<AttachmentsSectionProps> = ({
  workspaceId,
  entityType,
  entityId,
  readOnly = false,
  externalUploadProgress,
  variant = 'list',
}) => {
  const [isOpen, setIsOpen] = useState(true)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const {
    attachments,
    uploadProgress: internalUploadProgress,
    loading,
    error,
    uploadFile,
    deleteAttachment,
    getDownloadUrl,
  } = useAttachments({
    workspaceId,
    entityType,
    entityId,
  })

  // Use external upload progress if provided, otherwise use internal
  const uploadProgress = externalUploadProgress ?? internalUploadProgress

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      try {
        await uploadFile(file)
        toast.success(`Uploaded ${file.name}`)
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Upload failed'
        logger.logError(error as Error, 'ATTACHMENT_UPLOAD', {
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          entityType,
          entityId,
          workspaceId,
        })
        toast.error(`Failed to upload ${file.name}: ${errorMessage}`)
      }
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      onChange={handleFileChange}
      className="hidden"
      disabled={loading || readOnly}
      accept={FILE_INPUT_ACCEPT}
      aria-label="Select file to upload as attachment"
    />
  )

  if (variant === 'chips') {
    const open = async (attachmentId: string) => {
      try {
        window.open(await getDownloadUrl(attachmentId), '_blank')
      } catch {
        toast.error('Failed to open file')
      }
    }
    return (
      <section className="space-y-2">
        {fileInput}
        <div className="flex items-center gap-2">
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Files</span>
          <span className="text-[10.5px] font-semibold tabular-nums text-muted-foreground">{attachments.length}</span>
          {!readOnly && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={handleUploadClick}
              disabled={loading}
              className="ml-auto size-6 text-muted-foreground hover:text-foreground"
              aria-label="Attach file"
              title="Attach file"
            >
              <Plus className="size-3.5" />
            </Button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {attachments.map((attachment) => (
            <div key={attachment.id} className="group/file relative">
              <button
                type="button"
                onClick={() => void open(attachment.id)}
                className="flex h-9 max-w-[280px] items-center gap-2 rounded-lg border bg-card px-3 text-[13px] transition-colors hover:bg-secondary"
                title={attachment.file_name}
              >
                <FileText className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">{attachment.file_name}</span>
                <span className="shrink-0 text-[12px] text-muted-foreground">{formatFileSize(attachment.file_size_bytes)}</span>
              </button>
              {!readOnly && (
                <button
                  type="button"
                  aria-label={`Remove ${attachment.file_name}`}
                  onClick={() => {
                    if (confirm('Are you sure you want to delete this attachment?')) void deleteAttachment(attachment.id)
                  }}
                  className="absolute -right-1.5 -top-1.5 hidden size-5 items-center justify-center rounded-full border bg-card text-muted-foreground shadow-xs hover:text-foreground group-hover/file:flex"
                >
                  <X className="size-3" />
                </button>
              )}
            </div>
          ))}
        </div>
        {uploadProgress.length > 0 && (
          <AttachmentList attachments={[]} uploadProgress={uploadProgress} onDownload={getDownloadUrl} readOnly />
        )}
      </section>
    )
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileChange}
        className="hidden"
        disabled={loading || readOnly}
        accept={FILE_INPUT_ACCEPT}
        aria-label="Select file to upload as attachment"
      />

      <CollapsibleSectionHeader
        icon={<Paperclip className="size-4" />}
        title="Attachments"
        count={attachments.length}
        isOpen={isOpen}
        rightSlot={
          !readOnly && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={handleUploadClick}
              disabled={loading}
              className="size-6"
              title="Attach file"
            >
              <Upload className="size-4" />
            </Button>
          )
        }
      />

      <CollapsibleContent className="space-y-2 pt-2">
        {/* Error message */}
        {error && (
          <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md p-3">
            {error}
          </div>
        )}

        {/* Attachments list */}
        <AttachmentList
          attachments={attachments}
          uploadProgress={uploadProgress}
          onDownload={getDownloadUrl}
          onDelete={readOnly ? undefined : deleteAttachment}
          readOnly={readOnly}
        />
      </CollapsibleContent>

    </Collapsible>
  )
}
